import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  QrCode, Gift, Wallet, Star, Trophy, ArrowLeft, 
  Lightning, CreditCard, Users, Storefront, ArrowRight,
  CheckCircle
} from '@phosphor-icons/react';
import BottomNav from '../../components/BottomNav';

const HowItWorks = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('basics');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const sections = [
    { id: 'basics', label: 'Bases', icon: Lightning },
    { id: 'points', label: 'Points', icon: Star },
    { id: 'gifts', label: 'Cadeaux', icon: Gift },
    { id: 'faq', label: 'FAQ', icon: Users }
  ];

  const basicSteps = [
    {
      num: "1",
      icon: QrCode,
      title: "Scannez",
      subtitle: "Le QR code du marchand",
      description: "À chaque passage en caisse, ouvrez l'app et scannez. C'est instantané !",
      action: "Aller au scanner",
      actionPath: "/scanner",
      color: "#C3A365"
    },
    {
      num: "2",
      icon: Star,
      title: "Gagnez",
      subtitle: "Des points automatiquement",
      description: "Chaque scan vous rapporte des points. Plus vous visitez, plus vous cumulez.",
      highlight: "1 visite = 10 points minimum",
      color: "#145A3A"
    },
    {
      num: "3",
      icon: Trophy,
      title: "Profitez",
      subtitle: "De vos récompenses",
      description: "Convertissez vos points en crédit ou débloquez des avantages exclusifs.",
      action: "Voir mes récompenses",
      actionPath: "/rewards",
      color: "#C3A365"
    }
  ];

  const pointsInfo = [
    { 
      title: "Valeur des points", 
      content: "100 points = £1",
      detail: "Convertissez depuis votre portefeuille à tout moment"
    },
    { 
      title: "Comment gagner", 
      content: "Scanner = Points",
      detail: "Chaque visite chez un marchand partenaire vous rapporte au minimum 10 points"
    },
    { 
      title: "Expiration", 
      content: "Jamais !",
      detail: "Vos points n'expirent pas tant que votre compte est actif"
    }
  ];

  const tiers = [
    { name: "Bronze", min: "0+", perks: ["Accès au réseau", "Cumul de points", "Cartes cadeaux"], color: "#CD7F32" },
    { name: "Silver", min: "500+", perks: ["Bronze +", "Offres exclusives", "Priorité réseau"], color: "#C0C0C0" },
    { name: "Gold", min: "2000+", perks: ["Silver +", "Events VIP", "Cadeaux surprise"], color: "#C3A365" }
  ];

  const giftSteps = [
    { icon: Gift, text: "Choisissez le montant (£5 à £500)" },
    { icon: CreditCard, text: "Payez par carte bancaire" },
    { icon: Users, text: "Envoyez à un proche par email" },
    { icon: Storefront, text: "Utilisable dans tout le réseau" }
  ];

  const faqs = [
    { 
      q: "Comment scanner un QR code ?", 
      a: "Ouvrez l'app, allez sur l'onglet 'Scanner' en bas, et pointez votre caméra vers le QR code affiché par le marchand. La validation est instantanée !" 
    },
    { 
      q: "Mes points sont-ils universels ?", 
      a: "Oui ! Vos points sont cumulés sur votre compte et peuvent être utilisés chez n'importe quel marchand du réseau habitué." 
    },
    { 
      q: "Comment offrir une carte cadeau ?", 
      a: "Depuis votre portefeuille, cliquez sur 'Offrir', choisissez le montant, payez par carte, et envoyez-la à l'adresse email du destinataire." 
    },
    { 
      q: "Puis-je devenir marchand ?", 
      a: "Absolument ! Depuis votre profil, cliquez sur 'Devenir marchand'. L'inscription prend moins de 10 minutes." 
    },
    { 
      q: "Comment utiliser ma carte cadeau ?", 
      a: "Quand un marchand scanne votre QR code, vous pouvez choisir de payer avec votre solde de carte cadeau. Simple !" 
    }
  ];

  return (
    <div className="min-h-screen bg-[#18261E] pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-[#18261E]/95 backdrop-blur-xl border-b border-[#283D32] z-40">
        <div className="px-5 pt-6 pb-4">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-[#8BA89A] mb-3 hover:text-[#C3A365] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Retour</span>
          </button>
          <h1 className="text-2xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Comment ça marche
          </h1>
        </div>

        {/* Section Tabs */}
        <div className="px-5 pb-3 flex gap-2 overflow-x-auto scrollbar-hide">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all text-sm ${
                  isActive 
                    ? 'bg-[#C3A365] text-[#18261E] font-medium' 
                    : 'bg-[#1E2F26] text-[#8BA89A] hover:bg-[#283D32]'
                }`}
                data-testid={`tab-${section.id}`}
              >
                <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                {section.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {/* BASICS SECTION */}
        {activeSection === 'basics' && (
          <motion.div
            key="basics"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="px-5 py-6"
          >
            {/* Hero Message */}
            <div className="bg-gradient-to-br from-[#145A3A] to-[#1A734A] rounded-2xl p-6 mb-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#C3A365]/10 rounded-full blur-3xl" />
              <h2 className="text-xl text-white font-medium mb-2 relative z-10">
                En 3 étapes simples
              </h2>
              <p className="text-white/70 text-sm relative z-10">
                Gagnez des récompenses à chaque visite chez vos marchands préférés
              </p>
            </div>

            {/* Steps */}
            <div className="space-y-4">
              {basicSteps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.15 }}
                    className="bg-[#1E2F26] rounded-2xl p-5 border border-[#283D32] relative overflow-hidden"
                  >
                    {/* Step number badge */}
                    <div 
                      className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ backgroundColor: `${step.color}20`, color: step.color }}
                    >
                      {step.num}
                    </div>

                    <div className="flex gap-4">
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${step.color}15`, border: `1px solid ${step.color}30` }}
                      >
                        <Icon size={28} style={{ color: step.color }} weight="duotone" />
                      </div>
                      <div className="flex-1 pr-8">
                        <h3 className="text-white text-lg font-medium">{step.title}</h3>
                        <p className="text-[#C3A365] text-sm mb-2">{step.subtitle}</p>
                        <p className="text-[#8BA89A] text-sm leading-relaxed">{step.description}</p>
                        
                        {step.highlight && (
                          <div className="mt-3 inline-flex items-center gap-2 bg-[#283D32] px-3 py-1.5 rounded-full">
                            <Lightning size={12} className="text-[#C3A365]" weight="fill" />
                            <span className="text-[#C3A365] text-xs font-medium">{step.highlight}</span>
                          </div>
                        )}

                        {step.action && (
                          <button
                            onClick={() => navigate(step.actionPath)}
                            className="mt-3 flex items-center gap-2 text-[#C3A365] text-sm font-medium hover:underline"
                          >
                            {step.action} <ArrowRight size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* CTA */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              onClick={() => navigate('/scanner')}
              className="w-full mt-6 bg-[#C3A365] text-[#18261E] py-4 rounded-xl font-semibold flex items-center justify-center gap-2"
              data-testid="start-scanning-button"
            >
              <QrCode size={20} weight="bold" />
              Commencer à scanner
            </motion.button>
          </motion.div>
        )}

        {/* POINTS SECTION */}
        {activeSection === 'points' && (
          <motion.div
            key="points"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="px-5 py-6"
          >
            {/* Points Value Card */}
            <div className="bg-[#1E2F26] rounded-2xl p-6 mb-6 text-center border border-[#283D32]">
              <p className="text-[#8BA89A] text-sm mb-3">Valeur de vos points</p>
              <div className="flex items-center justify-center gap-4">
                <div>
                  <span className="text-5xl text-white font-light">100</span>
                  <span className="text-[#8BA89A] text-lg ml-2">pts</span>
                </div>
                <span className="text-[#C3A365] text-3xl">=</span>
                <div>
                  <span className="text-5xl text-[#C3A365] font-light">£1</span>
                </div>
              </div>
              <p className="text-[#8BA89A] text-sm mt-4">
                Convertissez à tout moment depuis votre portefeuille
              </p>
            </div>

            {/* Info Cards */}
            <div className="space-y-3 mb-6">
              {pointsInfo.map((info, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-[#1E2F26] rounded-xl p-4 border border-[#283D32]"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">{info.title}</span>
                    <span className="text-[#C3A365] font-semibold">{info.content}</span>
                  </div>
                  <p className="text-[#8BA89A] text-sm">{info.detail}</p>
                </motion.div>
              ))}
            </div>

            {/* Tiers */}
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Trophy size={18} className="text-[#C3A365]" />
              Niveaux de fidélité
            </h3>
            <div className="space-y-3">
              {tiers.map((tier, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="bg-[#1E2F26] rounded-xl p-4 border-l-4"
                  style={{ borderLeftColor: tier.color }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-lg" style={{ color: tier.color }}>{tier.name}</span>
                    <span className="text-[#8BA89A] text-sm">{tier.min} points</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tier.perks.map((perk, j) => (
                      <span key={j} className="text-xs bg-[#283D32] text-[#8BA89A] px-2 py-1 rounded-full">
                        {perk}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            <button
              onClick={() => navigate('/wallet')}
              className="w-full mt-6 bg-[#145A3A] text-white py-4 rounded-xl font-medium flex items-center justify-center gap-2 border border-[#1A734A]"
            >
              <Wallet size={18} />
              Voir mon portefeuille
            </button>
          </motion.div>
        )}

        {/* GIFTS SECTION */}
        {activeSection === 'gifts' && (
          <motion.div
            key="gifts"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="px-5 py-6"
          >
            {/* Hero */}
            <div className="bg-gradient-to-br from-[#C3A365]/20 to-[#C3A365]/5 border border-[#C3A365]/30 rounded-2xl p-6 mb-6 text-center">
              <Gift size={48} className="text-[#C3A365] mx-auto mb-3" weight="duotone" />
              <h2 className="text-xl text-white font-medium mb-2">Cartes Cadeaux</h2>
              <p className="text-[#8BA89A] text-sm">
                Offrez à vos proches. Valable chez tous les marchands du réseau.
              </p>
            </div>

            {/* Steps */}
            <h3 className="text-white font-medium mb-4">Comment ça marche</h3>
            <div className="space-y-3 mb-6">
              {giftSteps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-[#1E2F26] rounded-xl p-4 flex items-center gap-4 border border-[#283D32]"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#283D32] flex items-center justify-center flex-shrink-0">
                      <span className="text-[#C3A365] font-bold">{i + 1}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-1">
                      <Icon size={20} className="text-[#8BA89A]" />
                      <span className="text-white text-sm">{step.text}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Benefits */}
            <div className="bg-[#1E2F26] rounded-xl p-5 mb-6 border border-[#283D32]">
              <h4 className="text-white font-medium mb-3">Avantages</h4>
              <ul className="space-y-2">
                {[
                  "Montants de £5 à £500",
                  "Envoi instantané par email",
                  "Pas de frais cachés",
                  "Valable partout dans le réseau"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-[#8BA89A] text-sm">
                    <CheckCircle size={16} className="text-[#C3A365]" weight="fill" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={() => navigate('/send-gift')}
              className="w-full bg-[#C3A365] text-[#18261E] py-4 rounded-xl font-semibold flex items-center justify-center gap-2"
              data-testid="send-gift-button"
            >
              <Gift size={20} weight="bold" />
              Offrir une carte cadeau
            </button>
          </motion.div>
        )}

        {/* FAQ SECTION */}
        {activeSection === 'faq' && (
          <motion.div
            key="faq"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="px-5 py-6"
          >
            <p className="text-[#8BA89A] text-sm mb-6">
              Les réponses à vos questions les plus fréquentes
            </p>

            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-[#1E2F26] rounded-xl overflow-hidden border border-[#283D32]"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    className="w-full p-4 flex items-center justify-between text-left"
                    data-testid={`faq-${i}`}
                  >
                    <span className="text-white text-sm font-medium pr-4">{faq.q}</span>
                    <motion.span
                      animate={{ rotate: expandedFaq === i ? 180 : 0 }}
                      className="text-[#C3A365] flex-shrink-0"
                    >
                      ▼
                    </motion.span>
                  </button>
                  <AnimatePresence>
                    {expandedFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 text-[#8BA89A] text-sm leading-relaxed border-t border-[#283D32] pt-3">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>

            {/* Contact */}
            <div className="mt-8 bg-[#1E2F26] rounded-xl p-5 text-center border border-[#283D32]">
              <p className="text-white font-medium mb-2">Encore des questions ?</p>
              <p className="text-[#8BA89A] text-sm mb-4">
                Notre équipe est là pour vous aider
              </p>
              <button className="text-[#C3A365] text-sm font-medium">
                support@habitue.com
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

export default HowItWorks;
